import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { CheckCircle, XCircle, AlertCircle, Loader2, Home, ExternalLink } from "lucide-react";

const PurchaseSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'invalid'>('loading');
    const [message, setMessage] = useState('');
    const [countdown, setCountdown] = useState(5);

    const sessionId = searchParams.get("session_id");
    const orderId = searchParams.get("order_id");
    const redirectTo = searchParams.get("redirect_to") || "/";

    const verifySession = async () => {
        try {


            if (!sessionId) {
                setStatus('invalid');
                setMessage("Invalid payment session. No session ID provided.");
                return;
            }

            // Simulate API call - Replace with actual verification
            // const response = await fetch(
            //     `https://livewizard.westeurope.cloudapp.azure.com/checkout/verify-session?sessionId=${sessionId}`,
            //     {
            //         method: "GET",
            //         headers: {
            //             Authorization: `Bearer ${token}`,
            //             "Content-Type": "application/json",
            //         },
            //     }
            // );

            // const data = await response.json();

            // Simulate API delay
            setTimeout(() => {
                // Mock successful response
                const mockSuccess = true; // Replace with: data.success

                if (mockSuccess) {
                    setStatus('success');
                    setMessage("Payment verified successfully! Redirecting to your content...");

                    // Store purchase confirmation
                    localStorage.setItem(`purchase_${sessionId}`, JSON.stringify({
                        verified: true,
                        timestamp: new Date().toISOString(),
                        sessionId,
                        orderId
                    }));

                    // Redirect after delay
                    setTimeout(() => {
                        navigate(redirectTo, {
                            state: {
                                purchaseVerified: true,
                                sessionId,
                                orderId
                            }
                        });
                    }, 3000);
                } else {
                    setStatus('error');
                    setMessage("Payment verification failed. Please contact support.");
                }
            }, 2000);

        } catch (error) {
            console.error("Error verifying session:", error);
            setStatus('error');
            setMessage("An error occurred while verifying your payment. Please try again.");
        }
    };

    // Countdown timer
    useEffect(() => {
        if (status === 'success' && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [status, countdown]);

    useEffect(() => {
        if (sessionId) {
            verifySession();
        } else {
            setStatus('invalid');
            setMessage("No payment session detected.");
        }
    }, [sessionId]);

    const StatusIcon = () => {
        switch (status) {
            case 'loading':
                return <Loader2 className="h-16 w-16 text-blue-600 animate-spin" />;
            case 'success':
                return <CheckCircle className="h-16 w-16 text-green-600" />;
            case 'error':
                return <XCircle className="h-16 w-16 text-red-600" />;
            case 'invalid':
                return <AlertCircle className="h-16 w-16 text-yellow-600" />;
            default:
                return <Loader2 className="h-16 w-16 text-blue-600 animate-spin" />;
        }
    };

    const getStatusTitle = () => {
        switch (status) {
            case 'loading':
                return "Verifying Payment";
            case 'success':
                return "Payment Successful!";
            case 'error':
                return "Verification Failed";
            case 'invalid':
                return "Invalid Session";
            default:
                return "Processing";
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case 'loading':
                return "text-blue-600";
            case 'success':
                return "text-green-600";
            case 'error':
                return "text-red-600";
            case 'invalid':
                return "text-yellow-600";
            default:
                return "text-gray-600";
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full mx-auto">
                {/* Status Card */}
                <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                    <div className="flex justify-center mb-6">
                        <div className={`p-4 rounded-full ${status === 'success' ? 'bg-green-50' :
                            status === 'error' ? 'bg-red-50' :
                                status === 'invalid' ? 'bg-yellow-50' :
                                    'bg-blue-50'}`}>
                            <StatusIcon />
                        </div>
                    </div>

                    <h1 className={`text-2xl font-bold ${getStatusColor()} mb-3`}>
                        {getStatusTitle()}
                    </h1>

                    <p className="text-gray-600 mb-6">
                        {message}
                    </p>

                    {/* Session Details */}
                    {sessionId && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <div className="text-sm text-gray-500 mb-2">Transaction Details</div>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Session ID:</span>
                                    <code className="text-sm font-mono text-gray-800 truncate max-w-[180px]">
                                        {sessionId}
                                    </code>
                                </div>
                                {orderId && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Order ID:</span>
                                        <span className="font-medium">{orderId}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Countdown or Status Indicator */}
                    {status === 'success' && (
                        <div className="mb-6">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                                <span className="text-sm text-gray-600">
                                    Redirecting in {countdown} seconds...
                                </span>
                            </div>

                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        {status === 'success' && (
                            <>
                                <button
                                    onClick={() => navigate(redirectTo)}
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    <ExternalLink className="h-5 w-5" />
                                    Go to Content Now
                                </button>
                                <button
                                    onClick={() => navigate("/")}
                                    className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                                >
                                    <Home className="h-5 w-5" />
                                    Back to Home
                                </button>
                            </>
                        )}

                        {(status === 'error' || status === 'invalid') && (
                            <>
                                <button
                                    onClick={() => navigate("/support")}
                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
                                >
                                    Contact Support
                                </button>
                                <button
                                    onClick={() => navigate("/")}
                                    className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors"
                                >
                                    Return Home
                                </button>
                            </>
                        )}

                        {status === 'loading' && (
                            <button
                                disabled
                                className="w-full bg-gray-100 text-gray-400 font-medium py-3 px-6 rounded-lg cursor-not-allowed"
                            >
                                Please wait...
                            </button>
                        )}
                    </div>

                    {/* Help Text */}
                    <div className="mt-6 pt-6 border-t border-gray-100">
                        <p className="text-sm text-gray-500">
                            Need help?{" "}
                            <a
                                href="mailto:support@wizardfootball.com"
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                                Contact our support team
                            </a>
                        </p>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="mt-6 text-center">
                    <div className="text-xs text-gray-400 space-y-1">
                        <p>Transaction ID: {sessionId?.substring(0, 8)}...</p>
                        <p>Time: {new Date().toLocaleTimeString()}</p>
                    </div>
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
            <div className="absolute bottom-10 text-center">
                <div className="flex items-center justify-center gap-2 text-gray-400">
                    <span className="text-sm">Powered by</span>
                    <span className="font-bold text-gray-600">Wizard Football</span>
                </div>
            </div>
        </div>
    );
};

export default PurchaseSuccess;